import React, { useState, useContext  } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Button } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import * as Yup from 'yup';
import fetchApi from '../../api/fetchApi';
import ModalAlert from '../template/ModalAlert';
import { AuthContext } from '../../context/AuthContext';
import { Permit } from '../../types/types';

interface ModalUploadProps {
    isOpen: boolean;
    onClose: () => void;
    projectId?: number; 
}

const validationSchema = Yup.object({
    projectId: Yup.number().required('Este campo es requerido'),
    eacRevenue: Yup.number().required('Este campo es requerido'),
    eacCost: Yup.number().required('Este campo es requerido'),
    eacMargin: Yup.number().required('Este campo es requerido'),
    accumulatedRevenue: Yup.number().required('Este campo es requerido'),
    accumulatedCost: Yup.number().required('Este campo es requerido'),
    accumulatedProfit: Yup.number().required('Este campo es requerido'),
    accumulatedMargin: Yup.number().required('Este campo es requerido'),
    etcRevenue: Yup.number().required('Este campo es requerido'),
    etcCost: Yup.number().required('Este campo es requerido'),
    etcProfit: Yup.number().required('Este campo es requerido'),
    etcMargin: Yup.number().required('Este campo es requerido'),
    ytdRevenue: Yup.number().required('Este campo es requerido'),
    ytdCost: Yup.number().required('Este campo es requerido'),
    ytdProfit: Yup.number().required('Este campo es requerido'),
    ytdMargin: Yup.number().required('Este campo es requerido'),
    date: Yup.string().required('Este campo es requerido'),
});

const ModalExcelUpload: React.FC<ModalUploadProps> = ({ isOpen, onClose, projectId }) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewData, setPreviewData] = useState<any[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [modalAlertOpen, setModalAlertOpen] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string>('');
    const [alertType, setAlertType] = useState<'success' | 'error'>('success');
    const authContext = useContext(AuthContext);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const file = event.target.files[0];
            setSelectedFile(file);

            const reader = new FileReader();
            reader.onload = (e) => {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);

                const validationErrors: string[] = [];
                const validatedData = worksheet.map((row: any, index: number) => {
                    try {
                        validationSchema.validateSync(row, { abortEarly: false });
                        return { ...row };
                    } catch (validationError) {
                        if (validationError instanceof Yup.ValidationError) {
                            validationErrors.push(`Error en fila ${index + 2}: ${validationError.errors.join(', ')}`);
                        }
                        return null;
                    }
                }).filter(Boolean);

                setPreviewData(validatedData);
                setErrors(validationErrors);
            };

            reader.readAsArrayBuffer(file);
        }
    };

    const handleFileUpload = async () => {
        if (!selectedFile) {
            setAlertMessage('Por favor selecciona un archivo antes de subirlo.');
            setAlertType('error');
            setModalAlertOpen(true);
            return;
        }
    
        if (errors.length > 0) {
            setAlertMessage('Errores en el archivo:\n' + errors.join('\n'));
            setAlertType('error');
        } else {
            try {
                const formattedData = previewData.map((item) => ({
                    ...item,
                }));

                await fetchApi({ 
                    endpoint: '/close-months', 
                    method: 'POST', 
                    body: formattedData, 
                });
    
                setAlertMessage('Datos enviados correctamente');
                setAlertType('success');
                setTimeout(() => {
                    setModalAlertOpen(false);
                    onClose(); 
                }, 3000); 
            } catch (error) {
                setAlertMessage('Error al cerrar el proyecto, revisa los datos.');
                setAlertType('error');
            }
        }
        setModalAlertOpen(true);
    };

    const hasPermission = (moduleName: string, actionName: string): boolean => {
        return authContext?.user?.permits?.some((permit: Permit) =>
            permit.moduleName === moduleName && permit.actionName === actionName
        ) ?? false;
    }; 

    if (!hasPermission("projectClosing", "edit")) {
        return <div className="dashboard-container"><p>No tienes permiso para ver esta sección.</p></div>;
    }

    return (
        <Modal show={isOpen} onHide={onClose} size="lg">
            <Modal.Header closeButton className="modal-header-custom">
                <Modal.Title  className="white">Cargar datos desde Excel para cierre de mes de proyectos</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="ha-card">
                    <div className="ha-file-input-container">
                        <input
                            type="file"
                            accept=".xlsx"
                            onChange={handleFileChange}
                            id="file-input"
                            className="ha-file-input"
                        />
                        <label htmlFor="file-input" className="ha-file-label">
                            Seleccionar Archivo
                        </label>
                        <p className="ha-file-info">
                            {selectedFile ? `Archivo seleccionado: ${selectedFile.name}` : 'No has seleccionado ningún archivo'}
                        </p>
                    </div>
                    <button onClick={handleFileUpload} className="ha-btn">Subir Archivo</button>
                </div>

                {errors.length > 0 && (
                    <div className="error-messages">
                        <h4>Errores encontrados:</h4>
                        <ul>
                            {errors.map((error, index) => (
                                <li key={index}>{error}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </Modal.Body>
            <ModalAlert
                isOpen={modalAlertOpen}
                onClose={() => setModalAlertOpen(false)}
                message={alertMessage}
                type={alertType}
            />
        </Modal>
    );
};

export default ModalExcelUpload;
